import { useTranslation } from "react-i18next";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Skeleton } from "@/shared/ui";
import type { BudgetSubcategory } from "../../../types/api";
import { getSubcategoryColorHex } from "../../../utils/consts";
import { formatCompact } from "../../../utils/format";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

interface Props {
  data: Record<string, string | number>[];
  subcategories: BudgetSubcategory[];
  isLoading: boolean;
}

export default function ExpenseBreakdownChart({ data, subcategories, isLoading }: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const locale = i18n.language || "ru";

  if (isLoading) {
    return <Skeleton className="radius-sm flex-1" />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center flex-1 text-body-regular-sm content-base-secondary">
        {t("page.noData")}
      </div>
    );
  }

  function tickFormatter(value: number) {
    return formatCompact(value, locale);
  }

  return (
    <div className="flex-1">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 5, right: 5, left: 5, bottom: 5 }} barGap={4}>
          <CartesianGrid vertical={false} stroke="var(--chart-grid)" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: "var(--chart-axis)" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={tickFormatter}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "transparent" }} />
          {subcategories.map(function renderBar(sub, idx) {
            const isLast = idx === subcategories.length - 1;
            return (
              <Bar
                key={sub.id}
                dataKey={sub.id}
                name={(sub.name[locale as "ru" | "kk" | "en"] || sub.name.ru).replace(/^\d+\.\d+\s*/, "")}
                stackId="expenses"
                fill={getSubcategoryColorHex(sub.id, idx)}
                fillOpacity={0.85}
                activeBar={{ fillOpacity: 1 }}
                radius={isLast ? [4, 4, 0, 0] : undefined}
                maxBarSize={32}
              />
            );
          })}
          <Legend content={<ChartLegend />} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
